import { createContext } from 'react'
import type { ChatControl } from '../types/memberControls'
import type { Channel, ChannelType, ChatAuthorRole, ChatMessageMetadata, ChatViewerPresence, HuddleMetadata, HuddlePresence, Message, TypingPresence, WorkspaceQuota } from '../types/chat'
import type { TimelineBoardKey } from '../types/schema'
import type { MeetingProvider, MeetingStatus } from '../types/meetings'
import type { EntityReference } from '../types/entity'

export type ChatMeetingContext = {
  id: string
  title: string
  scheduledAt: string
  sourceProvider: MeetingProvider
  status: MeetingStatus
  agendaMarkdown: string
} | null

/** Optional reply / task-link parameters accepted by the write transaction. */
export interface SendMessageOptions {
  /** One-off write target. Omitting it preserves the active-channel send path. */
  channelId?: string
  replyToId?: string
  targetTaskIds?: string[]
  mentionedElementIds?: string[]
  mentionedUserIds?: string[]
  entityReferences?: EntityReference[]
  attachments?: File[]
  /** Additive structured metadata. It supplements metadata derived from message text. */
  metadata?: ChatMessageMetadata
}

/** Member entry surfaced to chat UIs — derived from `workspace_members`. */
export interface ChatMember {
  id: string
  uid: string
  name: string
  email: string
  role: ChatAuthorRole
  avatarUrl: string
}

/** Options accepted by `createChannel`. */
export interface CreateChannelOptions {
  name: string
  type: ChannelType
  /** uids to seed into `roles` as 'member' (creator is always 'admin'). */
  initialMemberIds?: string[]
  scopeNodeId?: string | null
}

/**
 * Returns a field object for channel creation scopeNodeId.
 * Returns the trimmed ID when non-empty string, else an empty object {}.
 */
export function channelScopeNodeIdField(scopeNodeId?: string | null): { scopeNodeId?: string } {
  const trimmed = typeof scopeNodeId === 'string' ? scopeNodeId.trim() : ''
  return trimmed ? { scopeNodeId: trimmed } : {}
}

export interface UpdateChannelMembersOptions {
  addMemberIds?: string[]
  removeMemberIds?: string[]
  promoteAdminIds?: string[]
  demoteAdminIds?: string[]
}

export interface CreateChannelDraft {
  name?: string
  type?: ChannelType
  initialMemberIds?: string[]
  scopeNodeId?: string | null
}

/**
 * Reactive distribution surface for the Group Chat feature. Holds the live
 * channel list, the active channel's message stream, and the parent workspace
 * quota frame, plus the guarded `sendMessage` write transaction.
 */
export interface ChatContextValue {
  activeChannelId: string | null
  channelsList: Channel[]
  messagesStream: Message[]
  workspaceQuota: WorkspaceQuota | null
  members: ChatMember[]
  huddlePresence: HuddlePresence[]
  viewerPresence: ChatViewerPresence[]
  huddleMetadata: HuddleMetadata | null
  typingPresence: TypingPresence[]
  /** Map of channelId → has-unread boolean (true == new messages since lastReadAt). */
  unreadByChannel: Record<string, boolean>
  /** Active hashtag filter (lowercase slug, no `#`). null when filter is off. */
  activeTagFilter: string | null
  isLoading: boolean
  contextPrompt: string
  contextMetadata: {
    activeGoalId: string | null
    activeTaskId: string | null
    activeSubTaskId: string | null
    currentBoard: TimelineBoardKey
  }
  meetingContext: ChatMeetingContext
  chatMode: ChatControl
  isChatOff: boolean
  setActiveChannelId: (channelId: string | null) => void
  setActiveTagFilter: (tag: string | null) => void
  setContextBoard: (board: TimelineBoardKey) => void
  setMeetingContext: (meeting: ChatMeetingContext) => void
  /**
   * Quota-guarded write. Rejects synchronously (before any DB call) when the
   * daily ceiling is reached; otherwise writes the message and atomically
   * increments `dailyChatsSentCount` on the parent workspace.
   */
  sendMessage: (textContent: string, options?: SendMessageOptions) => Promise<void>
  setHuddleState: (state: { isListening: boolean; isSpeaking: boolean }, channelId?: string) => Promise<void>
  /** Mark the active or supplied channel as read-up-to-now for the current user. */
  markRead: (channelId?: string) => Promise<void>
  /** Broadcast typing state to RTDB (debounced; auto-clears on disconnect). */
  setTyping: (isTyping: boolean) => Promise<void>
  /** Transactional channel creation. Returns the new channel id. */
  createChannel: (options: CreateChannelOptions) => Promise<string>
  pendingCreateChannelDraft: CreateChannelDraft | null
  requestCreateChannelDraft: (draft: CreateChannelDraft) => void
  clearCreateChannelDraft: () => void
  /** Rename a channel (re-slugs + swaps the unique slug lock). */
  renameChannel: (channelId: string, newName: string) => Promise<void>
  /** Delete a non-default channel and release its slug lock. */
  deleteChannel: (channelId: string) => Promise<void>
  /** Restore an archived channel to the active channel list. */
  restoreChannel: (channelId: string) => Promise<void>
  /** Permanently remove an archived channel shell and release its slug lock. */
  permanentlyDeleteArchivedChannel: (channelId: string) => Promise<void>
  /** Batch member/admin changes for the channel edit panel. */
  updateChannelMembers: (channelId: string, options: UpdateChannelMembersOptions) => Promise<void>
  /** Add a uid as a member of a private channel. */
  addMember: (channelId: string, uid: string) => Promise<void>
  /** Remove a uid from a private channel. */
  removeMember: (channelId: string, uid: string) => Promise<void>
  /** Leave a channel (admin-of-last-resort guard included client-side). */
  leaveChannel: (channelId: string) => Promise<void>
  /** Edit an owned sent message. */
  editMessage: (messageId: string, textContent: string) => Promise<void>
  /** Tombstone a sent message. Admins/Editors may moderate other users' messages. */
  deleteMessage: (messageId: string) => Promise<void>
  /** Undo a message tombstone. */
  restoreMessage: (messageId: string) => Promise<void>
}

export const ChatContext = createContext<ChatContextValue | undefined>(undefined)
